import { useState, useEffect, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import type { Artist } from '../types';

export const useArtists = (propArtists?: Artist[]) => {
  const [artistOptions, setArtistOptions] = useState<Artist[]>([]);
  const [isLoadingArtists, setIsLoadingArtists] = useState(false);
  const [artistOptionsLoaded, setArtistOptionsLoaded] = useState(false);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistPage, setArtistPage] = useState(1);
  const [hasMoreArtists, setHasMoreArtists] = useState(true);
  const [debouncedSearch] = useDebouncedValue(artistSearch, 300);

  // Fetch all artists for initial load
  const fetchAllArtists = async () => {
    setIsLoadingArtists(true);
    try {
      const res = await fetch('/artists/list?all=true', {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      setArtistOptions(data.artists);
      setArtistOptionsLoaded(true);
    } catch (err) {
      console.error('Failed to load artists', err);
    } finally {
      setIsLoadingArtists(false);
    }
  };

  // Fetch artists on mount
  useEffect(() => {
    fetch('/artists/list')
      .then((res) => res.json())
      .then((data) => setArtistOptions(data.artists))
      .catch((err) => console.error('Failed to load artists:', err));
  }, []);

  // Merge prop artists with fetched artists
  const mergedArtists = useMemo(() => {
    const allArtists = [...artistOptions];

    // Add any artists from propArtists that aren't already in artistOptions
    if (propArtists && Array.isArray(propArtists)) {
      propArtists.forEach((propArtist) => {
        const exists = allArtists.find((artist) => artist.id === propArtist.id);
        if (!exists) {
          allArtists.unshift(propArtist); // Add to beginning so selected artists appear first
        }
      });
    }

    return allArtists;
  }, [artistOptions, propArtists]);

  return {
    artistOptions,
    isLoadingArtists,
    artistOptionsLoaded,
    artistSearch,
    setArtistSearch,
    artistPage,
    setArtistPage,
    hasMoreArtists,
    setHasMoreArtists,
    debouncedSearch,
    fetchAllArtists,
    mergedArtists,
  };
};
